import { styled } from '@mui/material/styles';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { pxToRem, responsiveFontSizes } from '../../utils/getFontValue';
import {
	BasicTable,
	CurrentVsTotal,
	LargeButton,
	ScanOrEnterBarcodeForm,
} from '../../components';
import CheckIcon from '@mui/icons-material/Check';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import darkBarcode from '../../assets/images/darkbarcode.png';
import scannerIcon from '../../assets/images/Bar_code_scan.png';
import {
	DARK_MODE,
	ERROR_MESSAGE,
	PART_VALIDATION_ERROR,
	PRINTER_ERROR,
} from '../../constants';
import { connect } from 'react-redux';
import { useEffect, useState, useRef } from 'react';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import {
	bookSequenceNumber,
	getPartValidationDetailsForSerialNumber,
	revalidatePart,
	updatePartValidation,
	validatePart,
} from '../../redux/actionCreators/book';
import { useRedirectToNextScreen } from '../../hooks';
import { useOutletContext } from 'react-router-dom';
import { ReportModal } from '../../components';
import { getPartFamiliesToValidate } from '../../utils/utils';
import TakeOut from '../../components/TakeOut/TakeOut';
import MoveToMr from '../../components/MoveToMr/MoveToMr';
import { REDIRECT_URLS } from '../../constants';
import { OPERATIONS } from '../../constants';
import Exception from '../../components/Exception/Exception';
import styles from './BookingValidation.module.scss';
import SkipPart from '../../components/SkipPart/SkipPart';
import JlrButton from '../../components/JlrButton/JlrButton';

const ROW_HEIGHT = 38;

const PV_DEFAULT_COLUMNS = [
	{
		width: 300,
		title: 'Vin',
		field: 'serialNumber',
	},
	{
		width: 240,
		title: 'VC Number',
		field: 'parentPartNumber',
	},
	{
		width: 450,
		title: 'VC Description',
		field: 'parentPartDescription',
	},
];

const PVnG_DEFAULT_COLUMNS = [
	{
		width: 250,
		title: 'Part Family',
		field: 'familyName',
	},
	{
		width: 250,
		title: 'Part Number',
		field: 'partNumber',
		render: (
			prop,
			{
				openEllipsisPopupForEventWithText: handlePopoverOpen,
				closeEllipsisPopup: handlePopoverClose,
				onCopy,
			},
		) => {
			return (
				<Box sx={{ display: 'flex' }}>
					<Typography
						sx={{
							whiteSpace: 'nowrap',
							width: '90%',
							textAlign: 'left',
							letterSpacing: 0.5,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							fontSize: pxToRem(15),
							...responsiveFontSizes({ sm: 15, md: 17, lg: 21 }),
							opacity: !prop.hasOwnProperty('isErrorInScanningPartFamily')
								? 0.8
								: 1,
							color: !prop.hasOwnProperty('isErrorInScanningPartFamily')
								? null
								: prop.isErrorInScanningPartFamily
								? '#F17C7C'
								: '#82C1A7',
						}}
						onMouseEnter={(e) => handlePopoverOpen(e, prop.partNumber)}
						onMouseLeave={() => handlePopoverClose()}
						onDoubleClick={() => onCopy(prop.partNumber)}
					>
						{prop.partNumber}
					</Typography>
				</Box>
			);
		},
	},
	{
		width: 300,
		title: 'Part Description',
		field: 'partDescription',
		render: (
			prop,
			{
				openEllipsisPopupForEventWithText: handlePopoverOpen,
				closeEllipsisPopup: handlePopoverClose,
				onCopy,
			},
		) => {
			return (
				<Typography
					sx={{
						letterSpacing: 0.5,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						fontSize: pxToRem(15),
						...responsiveFontSizes({ sm: 15, md: 17, lg: 21 }),
						opacity: !prop.hasOwnProperty('isErrorInScanningPartFamily')
							? 0.8
							: 1,
						color: !prop.hasOwnProperty('isErrorInScanningPartFamily')
							? null
							: prop.isErrorInScanningPartFamily
							? '#F17C7C'
							: '#82C1A7',
					}}
					onMouseEnter={(e) => handlePopoverOpen(e, prop.partDescription)}
					onMouseLeave={() => handlePopoverClose()}
					onDoubleClick={() => onCopy(prop.partDescription)}
				>
					{prop.partDescription}
				</Typography>
			);
		},
	},
	{
		width: 150,
		title: 'Quantity',
		field: 'quantity',
		render: (prop) => {
			return (
				<CurrentVsTotal
					classNames={{
						currentNumber: {
							fontWeight: 700,
							fontSize: pxToRem(16),
							...responsiveFontSizes({ sm: 16, md: 18, lg: 24 }),
						},
						totalNumber: {
							fontSize: pxToRem(13),
							...responsiveFontSizes({ sm: 13, md: 15, lg: 19 }),
						},
					}}
					currentNumber={prop.partPosition}
					totalNumber={prop.quantity}
				/>
			);
		},
	},
	{
		width: 250,
		title: 'Serial Number',
		field: 'displaySerialNumber',
		render: (
			prop,
			{
				openEllipsisPopupForEventWithText: handlePopoverOpen,
				closeEllipsisPopup: handlePopoverClose,
				onCopy,
			},
		) => {
			return (
				<Typography
					sx={{
						letterSpacing: 0.5,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						fontSize: pxToRem(15),
						...responsiveFontSizes({ sm: 15, md: 17, lg: 21 }),
						opacity:
							!prop.skipReason &&
							(prop.displaySerialNumber || prop.scannedSerialNumber)
								? 0.8
								: 1,
						color:
							!prop.hasOwnProperty('isErrorInScanningPartFamily') &&
							!prop.skipReason &&
							(prop.displaySerialNumber || prop.scannedSerialNumber)
								? '#82C1A7'
								: '#F17C7C',
					}}
					onMouseEnter={(e) =>
						handlePopoverOpen(
							e,
							prop.displaySerialNumber || prop.scannedSerialNumber,
						)
					}
					onMouseLeave={() => handlePopoverClose()}
					onDoubleClick={() =>
						onCopy(prop.displaySerialNumber || prop.scannedSerialNumber)
					}
				>
					{prop.displaySerialNumber || prop.scannedSerialNumber || '-'}
				</Typography>
			);
		},
	},
	{
		width: 250,
		title: 'Part Validation & Genealogy',
		field: 'partValidation&Genealogy',
		render: ({
			isSelected,
			skipReason,
			scannedSerialNumber,
			scannedPartNumber,
		}) => (
			<Grid item display="flex">
				{(scannedSerialNumber || scannedPartNumber) && !skipReason ? (
					<CheckIcon sx={{ color: '#82C1A7' }} />
				) : (
					<ReportProblemRoundedIcon color="error" />
				)}
				<Typography
					component="span"
					sx={{
						paddingLeft: 1,
						letterSpacing: 0.5,
						fontWeight: isSelected ? 700 : 500,
						fontSize: pxToRem(15),
						...responsiveFontSizes({ sm: 15, md: 17, lg: 21 }),
						color:
							(scannedSerialNumber || scannedPartNumber) && !skipReason
								? '#82C1A7'
								: '#e47778',
					}}
				>
					{(scannedSerialNumber || scannedPartNumber) && !skipReason
						? 'Validated'
						: skipReason
						? 'Skipped'
						: 'Pending'}
				</Typography>
			</Grid>
		),
	},
	{
		width: 250,
		title: 'Reason to Skip',
		field: 'skipReason',
		columnStyle: {
			color: '#F17C7C',
		},
	},
];

const classNames = {
	paper: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	tableContainer: {
		height: 3 * ROW_HEIGHT,
	},
	tableContentContainer: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		backgroundColor: 'background.default',
		borderBottomLeftRadius: '5px',
		borderBottomRightRadius: '5px',
	},
};

const INTIAL_PART_FAMILY_SCAN_DATA = {
	scannedPartNumber: '',
	isPartNumberScanningDone: false,
	isErrorInScanningPartNumber: false,
	scannedSerialNumber: '',
	isSerialNumberScanningDone: false,
	isErrorInScanningSerialNumber: false,
};

const CustomiseTableButton = styled(Button)({
	fontSize: pxToRem(9),
	letterSpacing: '0.8px',
	fontWeight: 500,
	padding: '0.3rem 0.5rem',
	textTransform: 'capitalize',
	backgroundColor: '#2F7DFF',
	color: '#fff',
	'&:hover': {
		backgroundColor: '#2F7DFF',
	},
	...responsiveFontSizes({ sm: 11, md: 12, lg: 14 }),
});

const mapStateToProps = (state) => {
	return {
		receivePart: state.receivePart,
		partValidation: state.partValidation,
		themeMode: state.theme.themeMode,
		currentLineItem: state.station.currentLineItem,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getPartValidationDetailsForSerialNumber: (
		successCallback,
		errorCallback,
		sequenceNumber,
	) =>
		dispatch(
			getPartValidationDetailsForSerialNumber(
				successCallback,
				errorCallback,
				sequenceNumber,
			),
		),
	updatePartValidation: (data) => dispatch(updatePartValidation(data)),
	revalidatePart: (
		sequenceNumber,
		idToUpdate,
		payload,
		successCallback,
		errorCallback,
	) =>
		dispatch(
			revalidatePart(
				sequenceNumber,
				idToUpdate,
				payload,
				successCallback,
				errorCallback,
			),
		),
	validatePart: (sequenceNumber, payload, successCallback, errorCallback) =>
		dispatch(
			validatePart(sequenceNumber, payload, successCallback, errorCallback),
		),
	bookSequenceNumber: (sequenceNumber, successCallback, errorCallback) =>
		dispatch(
			bookSequenceNumber(sequenceNumber, successCallback, errorCallback),
		),
});

const OrderSuccess = ({ serialNumber }) => {
	return (
		<>
			<Typography variant="h3" sx={{ color: '#82C1A7' }}>
				Booking request for Serial No. / VIN{' '}
				<Typography variant="h3" component="span" fontWeight={700}>
					{serialNumber}
				</Typography>{' '}
				has been initiated successfully
			</Typography>
		</>
	);
};

const UpdateBookingValidationGenealogy = ({
	partValidation,
	selectedRows,
	themeMode,
	tableRows,
	setTableRows,
	revalidatePart,
	validatePart,
}) => {
	const { isPopupOpen, showMessageOverlay } = useMessageContextManager();
	const [partFamilyScannedData, setPartFamilyScannedData] = useState(
		INTIAL_PART_FAMILY_SCAN_DATA,
	);
	const [skippedSerialNumber, setSkippedSerialNumber] = useState('');
	const [skippedPartNumber, setSkippedPartNumber] = useState('');
	const [successMessage, setSuccessMessage] = useState('');
	const { scanJobDetails } = partValidation;

	const partNumberInputRef = useRef(null);
	const serialNumberInputRef = useRef(null);

	const onPartNumberSubmit = (scannedValue, resetBarcodeValue) => {
		const {
			validParts,
			sequenceNumber,
			familyId,
			partNumber,
			scanJobResultId,
			skipReason,
			partNumberScanningRequired,
			genealogyRequired,
			partPosition,
			scannedPartNumber,
		} = selectedRows[0];

		const successCallback = (updatedPartFamilyScannedData = {}) => {
			closeMesssageOverlay();
			setSuccessMessage('Part Matched');
			setPartFamilyScannedData({
				...partFamilyScannedData,
				scannedPartNumber: scannedValue,
				isPartNumberScanningDone: true,
				isErrorInScanningPartNumber: false,
				...updatedPartFamilyScannedData,
			});
			setTimeout(() => {
				setSuccessMessage('');
				resetBarcodeValue();
			}, 3000);
		};

		const errorCallback = () => {
			const rowsData = tableRows.map((partFamily) => {
				if (
					partFamily.familyId === familyId &&
					partFamily.partPosition === partPosition
				)
					return {
						...partFamily,
						scannedValue,
						skippedPartNumber: scannedValue,
						isErrorInScanningPartFamily: true,
					};
				return { ...partFamily };
			});
			setTableRows(rowsData);
			closeMesssageOverlay();
			setSkippedPartNumber(scannedValue);
			setPartFamilyScannedData((prev) => ({
				...prev,
				isPartNumberScanningDone: false,
				isErrorInScanningPartNumber: true,
			}));
			resetBarcodeValue();
			showMessageOverlay({
				contextText:
					PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Scanning Part Number',
				info: 'Part did not match',
				error: true,
			});
		};

		const onlyPartNoScanningSuccessCallback = () => {
			successCallback({ ...INTIAL_PART_FAMILY_SCAN_DATA });
		};

		const closeMesssageOverlay = showMessageOverlay({
			contextText: 'Scanning Part Number.',
			loading: true,
		});

		setPartFamilyScannedData((prev) => ({
			...prev,
			scannedPartNumber: scannedValue,
		}));

		const payload = {
			familyId,
			partNumber,
			scannedPartNumber: scannedValue,
			scannedSerialNumber: '',
		};

		if (partNumberScanningRequired) {
			if (skipReason || scannedPartNumber) {
				revalidatePart(
					sequenceNumber,
					scanJobResultId,
					{ ...payload },
					() => {
						onlyPartNoScanningSuccessCallback();
					},
					(error) => {
						errorCallback();
					},
				);
			} else {
				validatePart(
					sequenceNumber,
					{ ...payload },
					() => {
						onlyPartNoScanningSuccessCallback();
					},
					(error) => {
						errorCallback();
					},
				);
			}
		}
		// if (genealogyRequired && partNumberScanningRequired) {
		// 	if (validParts.includes(scannedValue)) {
		// 		validatePart(
		// 			sequenceNumber,
		// 			{ ...payload },
		// 			() => {
		// 				onlyPartNoScanningSuccessCallback();
		// 				successCallback();
		// 			},
		// 			(error) => {
		// 				logError(`Part number validation ${error}`);
		// 				errorCallback();
		// 			},
		// 		);
		// 	} else {
		// 		logError('Part did not match');
		// 		errorCallback();
		// 	}
		// }
	};

	const onSerialNumberSubmit = (scannedValue, resetBarcodeValue) => {
		const {
			sequenceNumber,
			familyId,
			partNumber,
			scanJobResultId,
			skipReason,
			partNumberScanningRequired,
			genealogyRequired,
			partPosition,
			scannedSerialNumber,
		} = selectedRows[0];

		const successCallback = () => {
			closeMesssageOverlay();
			setSuccessMessage('Serial Number Matched');
			setPartFamilyScannedData(INTIAL_PART_FAMILY_SCAN_DATA);
			setTimeout(() => {
				setSuccessMessage('');
				resetBarcodeValue();
			}, 3000);
		};

		const errorCallback = (error) => {
			const rowsData = tableRows.map((partFamily) => {
				if (
					partFamily.familyId === familyId &&
					partFamily.partPosition === partPosition
				)
					return {
						...partFamily,
						scannedValue,
						skippedSerialNumber: scannedValue,
						displaySerialNumber: scannedValue,
						isErrorInScanningPartFamily: true,
					};
				return { ...partFamily };
			});
			setTableRows(rowsData);
			setSkippedSerialNumber(scannedValue);
			closeMesssageOverlay();
			setPartFamilyScannedData((prev) => ({
				...prev,
				isSerialNumberScanningDone: false,
				isErrorInScanningSerialNumber: true,
			}));
			resetBarcodeValue();
			showMessageOverlay({
				contextText:
					PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Scanning Serial Number',
				info: error.response.data.message,
				error: true,
			});
		};

		const closeMesssageOverlay = showMessageOverlay({
			contextText: 'Scanning Serial Number.',
			loading: true,
		});

		setPartFamilyScannedData((prev) => ({
			...prev,
			scannedSerialNumber: scannedValue,
		}));

		const payload = {
			familyId,
			partNumber,
			scannedPartNumber:
				genealogyRequired && !partNumberScanningRequired
					? ''
					: partFamilyScannedData.scannedPartNumber,
			scannedSerialNumber: scannedValue,
		};

		if (skipReason || scannedSerialNumber) {
			revalidatePart(
				sequenceNumber,
				scanJobResultId,
				{ ...payload },
				() => {
					successCallback();
				},
				(error) => {
					errorCallback(error);
				},
			);
		} else {
			validatePart(
				sequenceNumber,
				{ ...payload },
				() => {
					successCallback();
				},
				(error) => {
					errorCallback(error);
				},
			);
		}
	};

	useEffect(() => {
		if (partValidation?.sequenceNumber) {
			const rowsData = getPartFamiliesToValidate(
				partValidation?.scanJobDetails,
				partValidation?.sequenceNumber,
			);

			setTableRows(rowsData);
		}
	}, [partValidation]);

	const isSkipDisabled = () => {
		const selectedRow = selectedRows[0];
		const latestPartToSkip = tableRows.find(
			(row) =>
				row.familyId === selectedRow.familyId &&
				!(row.scannedPartNumber || row.scannedSerialNumber || row.skipReason),
		);
		if (selectedRow.partPosition === latestPartToSkip.partPosition)
			return false;
		return true;
	};

	const partSkipSuccessCallback = (data, closeSkipSerialNumberDialog) => {
		const { scanJobDetails, sequenceNumber } = data;
		const rowsData = getPartFamiliesToValidate(scanJobDetails, sequenceNumber);
		setTableRows(rowsData);
		closeSkipSerialNumberDialog();
		setSuccessMessage('Part Skipped');
		setPartFamilyScannedData(INTIAL_PART_FAMILY_SCAN_DATA);
		setTimeout(() => {
			setSuccessMessage('');
		}, 3000);
	};

	const partSkipErrorCallback = (error, closeSkipSerialNumberDialog) => {
		closeSkipSerialNumberDialog();
		showMessageOverlay({
			contextText:
				PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Skipping Part Family',
			info: error.response.data.message,
			error: true,
		});
	};

	const renderInputFields = () => {
		const {
			partNumberScanningRequired,
			genealogyRequired,
			isErrorInScanningPartFamily,
		} = selectedRows[0];

		if (
			partNumberScanningRequired &&
			!partFamilyScannedData.isPartNumberScanningDone
		)
			return (
				<Grid
					item
					container
					direction="column"
					alignItems="end"
					columnSpacing={2}
					sx={{ opacity: selectedRows.length == 1 ? '100%' : '50%' }}
				>
					<Grid item display="flex" alignItems="center">
						{isErrorInScanningPartFamily &&
						partFamilyScannedData.isErrorInScanningPartNumber ? (
							<Typography variant="h3" color="error">
								RESCAN PART NO.
							</Typography>
						) : (
							<Typography variant="h3" sx={{ color: '#27C8F7' }}>
								SCAN PART NO.
							</Typography>
						)}
						<Box
							item
							sx={{
								height: { xs: 35, md: 40, lg: 60 },
								width: { xs: 75, md: 85, lg: 130 },
							}}
						>
							<img
								src={themeMode === DARK_MODE ? darkBarcode : scannerIcon}
								alt=""
								style={{
									height: '100%',
									width: '100%',
									objectFit: 'cover',
								}}
							/>
						</Box>
					</Grid>
					<Grid item xs={12} sx={{ width: { sm: '75%', md: '60%' } }} pt={1}>
					<ScanOrEnterBarcodeForm
							enableAutoEnter={
								genealogyRequired && partNumberScanningRequired ? false : true
							}
							inputRef={partNumberInputRef}
							inputProps={{
								disabled: selectedRows.length !== 1,
							}}
							onSubmit={onPartNumberSubmit}
						/>
					</Grid>
				</Grid>
			);
		if (genealogyRequired && !partFamilyScannedData.isSerialNumberScanningDone)
			return (
				<Grid
					item
					container
					direction="column"
					alignItems="end"
					columnSpacing={2}
					sx={{ opacity: selectedRows.length == 1 ? '100%' : '50%' }}
				>
					<Grid item display="flex" alignItems="center">
						{isErrorInScanningPartFamily &&
						partFamilyScannedData.isErrorInScanningSerialNumber ? (
							<Typography variant="h3" color="error">
								RESCAN SERIAL NUMBER
							</Typography>
						) : (
							<Typography variant="h3" sx={{ color: '#27C8F7' }}>
								SCAN SERIAL NUMBER
							</Typography>
						)}
						<Box
							item
							sx={{
								height: { xs: 35, md: 40, lg: 60 },
								width: { xs: 75, md: 85, lg: 130 },
							}}
						>
							<img
								src={themeMode === DARK_MODE ? darkBarcode : scannerIcon}
								alt=""
								style={{
									height: '100%',
									width: '100%',
									objectFit: 'cover',
								}}
							/>
						</Box>
					</Grid>
					<Grid item xs={12} sx={{ width: { sm: '75%', md: '60%' } }} pt={1}>
						<ScanOrEnterBarcodeForm
							enableAutoEnter={
								genealogyRequired && partNumberScanningRequired ? false : true
							}
							inputRef={serialNumberInputRef}
							inputProps={{
								disabled: selectedRows.length !== 1,
							}}
							onSubmit={onSerialNumberSubmit}
						/>
					</Grid>

				</Grid>
			);
	};

	useEffect(() => {
		if (!isPopupOpen) {
			if (partNumberInputRef.current) partNumberInputRef.current.focus();
			if (serialNumberInputRef.current) serialNumberInputRef.current.focus();
		}
	}, [selectedRows, isPopupOpen]);

	return (
		<>
			<Grid item xs={5} display="flex" flexWrap="wrap" alignItems="flex-end">
				<Grid item pt={1}>
					{!successMessage &&
						selectedRows.length === 1 &&
						!(
							selectedRows[0]?.skipReason ||
							selectedRows[0]?.scannedSerialNumber ||
							selectedRows[0]?.scannedPartNumber
						) && (
							<SkipPart
								skippedPartNumber={selectedRows[0].skippedPartNumber}
								skippedSerialNumber={selectedRows[0].skippedSerialNumber}
								disableBtn={isSkipDisabled()}
								hoverMessage="Please select the quantity in sequencial manner"
								successCallback={partSkipSuccessCallback}
								errorCallback={partSkipErrorCallback}
								currentPartNumber={scanJobDetails.findIndex(
									(part) => part.familyId === selectedRows[0]?.familyId,
								)}
							/>
						)}
				</Grid>
				{successMessage && (
					<>
						<CheckIcon
							color="primary"
							sx={{
								fontSize: {
									lg: 34,
								},
							}}
						/>
						<Typography
							variant="h4"
							component="span"
							color="primary"
							fontWeight={400}
							paddingLeft={1}
						>
							{successMessage}
						</Typography>
					</>
				)}
			</Grid>
			<Grid item xs={7}>
				{!successMessage && !!selectedRows.length && renderInputFields()}
			</Grid>
		</>
	);
};

const BookingValidationGenealogy = ({
	receivePart,
	partValidation,
	themeMode,
	getPartValidationDetailsForSerialNumber,
	revalidatePart,
	validatePart,
	currentLineItem,
	bookSequenceNumber,
}) => {
	const [tableRows, setTableRows] = useState(null);
	const { handleNext } = useOutletContext();
	const { showMessageOverlay } = useMessageContextManager();
	const redirect = useRedirectToNextScreen();

	useEffect(() => {
		if (receivePart.serialNumber || currentLineItem.serialNumber) {
			getPartValidationDetailsForSerialNumber(
				(data) => {
					const rowsData = getPartFamiliesToValidate(
						data.scanJobDetails,
						data.sequenceNumber,
					);

					setTableRows(rowsData);
				},
				(error) => {
					updatePartValidation({});
					showMessageOverlay({
						contextText:
							PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Part Validation',
						info: error?.response?.data?.message,
						error: true,
					});
				},
				receivePart.serialNumber || currentLineItem.serialNumber,
			);
		}
	}, [receivePart.serialNumber, currentLineItem.serialNumber]);

	useEffect(() => {
		const { isExceptionRaised } = partValidation;
		if ((tableRows !== null && tableRows.length === 0) || isExceptionRaised)
			redirect(() => handleNext());
	}, [tableRows]);

	const [isDetailsModalOpen, setIsDetailModalOpen] = useState(false);

	const openDetailsModal = () => setIsDetailModalOpen(true);
	const closeDetailsModal = () => {
		setIsDetailModalOpen(false);
	};

	const renderCustomError = (partNumbers) => {
		return (
			<div
				style={{ height: '250px', overflowY: 'auto' }}
				className={styles.scrollBar}
			>
				{partNumbers.map((partNumber) => (
					<p>{partNumber}</p>
				))}
			</div>
		);
	};

	const redirectToHomeScreen = () =>
		redirect(null, {
			operation: OPERATIONS.RECEIVE_PART,
			redirectUrl: REDIRECT_URLS[OPERATIONS.RECEIVE_PART],
		});

	const redirectToBookingScreen = () => redirect(() => handleNext());

	const handleBooking = () => {
		bookSequenceNumber(
			partValidation.sequenceNumber,
			() => {
				const closeOverlay = showMessageOverlay({
					contextText: ' ',
					info: ' ',
					success: true,
					render: () => (
						<OrderSuccess serialNumber={partValidation.serialNumber} />
					),
				});
				setTimeout(() => {
					closeOverlay();
					redirectToHomeScreen();
				}, 3000);
			},
			(error) => {
				showMessageOverlay({
					contextText: ERROR_MESSAGE.ERROR_CONTEXT_TEXT + 'Booking.',
					info: error?.response?.data?.message,
					error: true,
				});
			},
		);
	};

	const isExceptionAllowed = () => {
		if (
			tableRows.every(
				(part) =>
					part.skipReason ||
					part?.scannedSerialNumber ||
					part?.scannedPartNumber,
			)
		)
			return true;

		const partFamilyNames = [
			...new Set(
				tableRows
					.filter(
						(part) =>
							!part?.skipReason &&
							(!part?.scannedSerialNumber || !part?.scannedPartNumber),
					)
					.map((part) => part.familyName),
			),
		];
		showMessageOverlay({
			contextText:
				PRINTER_ERROR.ERROR_CONTEXT_TEXT +
				`Part not scanned ${partFamilyNames.length}`,
			info: 'Below are the part families on which validation or skip is required.',
			render: () => renderCustomError(partFamilyNames),
			error: true,
		});
		return false;
	};

	if (!partValidation || !Object.keys(partValidation)?.length) return null;

	return (
		<BasicTable
			listId="part-validation"
			rows={[{ ...partValidation }]}
			columns={[...PV_DEFAULT_COLUMNS]}
			defaultColumns={[...PV_DEFAULT_COLUMNS]}
			subtitle="KEY ATTRIBUTES"
			tableStyles={{
				paper: classNames.paper,
				tableContainer: classNames.tableContainer,
			}}
		>
			<Box sx={classNames.tableContentContainer}>
				<Grid
					container
					paddingX={2}
					paddingY={1}
					alignItems="center"
					columnSpacing={2}
				>
					<Grid item>
						<Button
							variant="contained"
							disableRipple
							sx={{
								color: '#E47778',
								background: 'black',
								cursor: 'unset',
								'&:hover': {
									backgroundColor: 'black',
								},
							}}
						>
							Genealogy Pending {tableRows && tableRows.length}
						</Button>
					</Grid>
					<Grid item>
						<CustomiseTableButton onClick={openDetailsModal}>
							View Report
						</CustomiseTableButton>
					</Grid>
					<Grid item flexGrow={1}>
						<Grid container justifyContent="flex-end" columnSpacing={2}>
							{tableRows?.some((row) => row?.familyInterlock) && (
								<Grid item>
									<Exception
										btnStyles={{
											fontSize: pxToRem(15),
											...responsiveFontSizes({ sm: 15, md: 18, lg: 26 }),
											padding: {
												xs: '0.3rem 1rem',
												lg: '1rem 1.7rem',
											},
										}}
										isExceptionAllowed={isExceptionAllowed}
										redirectToNextScreen={redirectToBookingScreen}
									/>
								</Grid>
							)}
							<Grid item>
								<TakeOut
									partDetails={partValidation}
									btnStyles={{
										fontSize: pxToRem(15),
										...responsiveFontSizes({ sm: 15, md: 18, lg: 26 }),
										padding: {
											xs: '0.3rem 1rem',
											lg: '1rem 1.7rem',
										},
									}}
									redirectToNextScreen={redirectToHomeScreen}
								/>
							</Grid>
							<Grid item>
								<MoveToMr
									partDetails={{
										...partValidation,
										serialNumber: partValidation?.serialNumber || receivePart?.serialNumber,
										sequenceNumber: partValidation?.sequenceNumber || receivePart?.serialNumber,
									}}
								/>
							</Grid>
							<Grid item>
								{/* <LargeButton
									name="NEXT OPERATION"
									backgroundColor="#82C1A7"
									sx={{
										fontSize: pxToRem(15),
										...responsiveFontSizes({ sm: 15, md: 18, lg: 26 }),
										padding: {
											xs: '0.3rem 1rem',
											lg: '1rem 1.7rem',
										},
									}}
									disabled={tableRows?.some(
										(row) =>
											!(
												row?.scannedSerialNumber ||
												row?.scannedPartNumber ||
												row?.skipReason
											) ||
											(row?.skipReason &&  row?.familyInterlock),
									)}
									onClick={redirectToBookingScreen}
								/> */}
								<JlrButton
									variant="success"
									size="large"
									onClick={handleBooking}
									sx={{
										fontSize: pxToRem(15),
										...responsiveFontSizes({ sm: 15, md: 18, lg: 26 }),
										padding: {
											xs: '0.3rem 1rem',
											lg: '1rem 1.7rem',
										},
									}}
								>
									Book
								</JlrButton>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				<BasicTable
					listId="genealogy-page"
					rows={tableRows || []}
					columns={[...PVnG_DEFAULT_COLUMNS]}
					defaultColumns={[...PVnG_DEFAULT_COLUMNS]}
					showCheckBox
					tableStyles={{
						paper: classNames.paper,
						tableContainer: classNames.tableContentContainer,
					}}
				>
					{(selectedRows) => (
						<>
							<Divider />
							<Grid
								container
								item
								justifyContent="space-between"
								alignItems="bottom"
								paddingX={3}
								paddingBottom={2}
							>
								<Grid xs container>
									<UpdateBookingValidationGenealogy
										themeMode={themeMode}
										selectedRows={selectedRows}
										revalidatePart={revalidatePart}
										validatePart={validatePart}
										tableRows={tableRows}
										setTableRows={setTableRows}
										partValidation={partValidation}
									/>
								</Grid>
							</Grid>
						</>
					)}
				</BasicTable>
				{partValidation.scanJobDetails &&
					partValidation.scanJobDetails.length > 0 && (
						<ReportModal
							open={isDetailsModalOpen}
							handleClose={closeDetailsModal}
							rows={[partValidation]}
							columns={[...PV_DEFAULT_COLUMNS]}
							defaultColumns={[...PV_DEFAULT_COLUMNS]}
							title="Current KSPEC"
							subtitle="Key Attributes"
							tableStyles={{ paper: classNames.paper }}
							scannableParts={partValidation.scanJobDetails}
						/>
					)}
			</Box>
		</BasicTable>
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(BookingValidationGenealogy);
